import { Box, Flex } from '@chakra-ui/react';
import throttle from 'lodash.throttle';
import { memo, useEffect, useRef } from 'react';

import { ChatPanelProps, MessageComponentProps, MessageType } from './Chat.types';
import { BotMessage } from './Messages/BotMessage';
import { ButtonMessage } from './Messages/ButtonMessage';
import { DesignMessage } from './Messages/DesignMessage';
import { GeneratingMessage } from './Messages/GeneratingMessage';
import { SuggestionMessage } from './Messages/SuggestionMessage';
import { UserMessage } from './Messages/UserMessage';

/**
 * Render the chat panel to display all messages
 *
 * @param messages - The chat messages
 * @param isDisabled - Disable the chat input
 * @param autoScrollToBottom - Auto scroll to the bottom of the chat panel
 * @param onUpdate - Handle all events triggered by the message
 */
export const ChatPanel = memo(
  ({ messages, isDisabled = false, autoScrollToBottom = false, onUpdate = () => {}, config }: ChatPanelProps) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesRef = useRef<HTMLDivElement>(null);
    const scrollingRef = useRef(false);
    const scrollObserverRef = useRef<MutationObserver | null>(null);
    const throttleScrollToBottom = useRef<(() => void) | null>(null);

    // Initilize the throttle function
    if (throttleScrollToBottom.current === null) {
      throttleScrollToBottom.current = throttle(
        () => {
          if (!messagesRef.current || scrollingRef.current) return;

          // Optimize scrollIntoView calls for performance
          const threshold = 5;
          const { scrollTop, scrollHeight, clientHeight } = messagesRef.current;
          const atBottom = scrollTop + clientHeight + threshold >= scrollHeight;
          if (atBottom) return;

          // Set scrolling state
          scrollingRef.current = true;

          // Scroll to bottom.
          // NOTE: Scroll animation is not working on the Safari, if it needs, use the polyfill for this behavior property.
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

          // Prevent to call scrollIntoView to solve the scrolling lag issue when it moving scroll.
          setTimeout(() => {
            // Reset scrolling state
            scrollingRef.current = false;
          }, 1000);
        },
        200,
        { trailing: true, leading: true },
      );
    }

    // Observe the change while generating the design and keep move scroll to bottom.
    useEffect(() => {
      if (!messagesRef?.current || !throttleScrollToBottom.current || !autoScrollToBottom) return;

      // Run the observer
      scrollObserverRef.current = new MutationObserver(throttleScrollToBottom.current);
      scrollObserverRef.current.observe(messagesRef.current, {
        subtree: true,
        childList: true,
        characterData: true,
      });

      // Cleanup the observer
      return () => {
        if (scrollObserverRef.current) {
          scrollObserverRef.current.disconnect();
          scrollObserverRef.current = null;
        }
      };
    }, [autoScrollToBottom]);

    const ChatPanelTestId = 'chat-panel';

    return (
      <Flex h='full' flexDir='column' overflowY='scroll' gap='4' pr='4' ref={messagesRef} data-testid={ChatPanelTestId}>
        {messages.map((item, index) => (
          <MessageComponent
            key={`${item.type}-${item.createdAt}`}
            data={item}
            index={index}
            isDisabled={isDisabled}
            config={config}
            onUpdate={onUpdate}
          />
        ))}
        <Flex ref={messagesEndRef}>
          {/* 
            Notes: Firefox bug fix.
            Elements with zero height and 'overflow: Scroll', there was a bug where 0px was hidden during CRP and the flex gap was not handled correctly.
          */}
          <Box h='1px'></Box>
        </Flex>
      </Flex>
    );
  },
);

ChatPanel.displayName = 'ChatPanel';

/**
 * Return Dynamic Message Component
 */

const COMPONENT_MAP = {
  [MessageType.BOT]: BotMessage,
  [MessageType.BOT_GENERATING]: GeneratingMessage,
  [MessageType.USER]: UserMessage,
  [MessageType.DESIGN]: DesignMessage,
  [MessageType.SUGGESTION]: SuggestionMessage,
  [MessageType.BUTTON]: ButtonMessage,
};

const MessageComponent = memo(({ data, index, isDisabled = false, config, onUpdate }: MessageComponentProps) => {
  const Component = COMPONENT_MAP[data.type];

  if (!Component) return null;

  return <Component data={data} index={index} isDisabled={isDisabled} config={config} onUpdate={onUpdate} />;
});

MessageComponent.displayName = 'MessageComponent';
